/**
 * GapDeliveryNote MDXEditor Plugin
 *
 * Renders a delivery notes block with read-more functionality.
 * The content inside is editable in edit mode.
 *
 * MARKDOWN SYNTAX (containerDirective):
 * :::GapDeliveryNote
 * Your delivery notes content here
 * :::
 */

import { DirectiveDescriptor, DirectiveEditorProps, usePublisher, insertDirective$, NestedLexicalEditor, useCellValue, readOnly$ } from '@mdxeditor/editor'
import type { ContainerDirective } from 'mdast-util-directive'
import type { RootContent } from 'mdast'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from '@mui/material/Tooltip'
import { themeConfig } from '@gap/shared/theme'
import GapLink from 'reusable/mui/links/GapLink'
import { useState, useCallback, useRef, useEffect, useSyncExternalStore } from 'react'
import { useThemeStore } from 'theme/ThemeStore'
import { CacheHandler } from 'reusable/utils/cache'

const DIRECTIVE_NAME = 'GapDeliveryNote'
const COLLAPSED_LINES = 4
// Must be slightly larger than what 4 lines of mixed content (paragraphs, bold
// headings, list items) actually occupies, so short content doesn't false-trigger.
const EXPANSION_THRESHOLD = 160

const deliveryNoteColors = {
   light: {
      background: '#fff',
      border: themeConfig.primary['900'],
      text: themeConfig.primary['900'],
      icon: themeConfig.primary['900'],
   },
   dark: {
      background: '#1A1F2E',
      border: themeConfig.primary['300'],
      text: themeConfig.primary['200'],
      icon: themeConfig.primary['300'],
   },
}

const getContent = (node: ContainerDirective) => node.children

const getUpdatedMdastNode = (node: ContainerDirective, children: RootContent[]): ContainerDirective => ({
   ...node,
   children: children as ContainerDirective['children'],
})

/**
 * Subscribe to delivery notes visibility via useSyncExternalStore.
 * This bypasses React state propagation issues inside MDXEditor's Lexical
 * decorator boundary by subscribing directly to the window CustomEvent
 * that CacheHandler dispatches when the value changes.
 */
const CACHE_KEY = 'show-delivery-notes'
const cache = new CacheHandler<boolean>({ key: CACHE_KEY, cacheLocation: 'sessionStorage' })

function subscribeToDeliveryNotes(onStoreChange: () => void): () => void {
   const handler = () => onStoreChange()
   window.addEventListener(CACHE_KEY, handler)
   return () => window.removeEventListener(CACHE_KEY, handler)
}

function getDeliveryNotesSnapshot(): boolean {
   return cache.get() ?? true
}

function GapDeliveryNoteComponent(_props: DirectiveEditorProps) {
   const isReadOnly = useCellValue(readOnly$)
   const colors = deliveryNoteColors[useThemeStore().dark ? 'dark' : 'light']
   const showDeliveryNotes = useSyncExternalStore(subscribeToDeliveryNotes, getDeliveryNotesSnapshot)
   const [expanded, setExpanded] = useState(false)
   const [isEditing, setIsEditing] = useState(false)
   const wrapperRef = useRef<HTMLDivElement>(null)
   const contentRef = useRef<HTMLDivElement>(null)
   const [needsExpansion, setNeedsExpansion] = useState(false)

   const toggle = useCallback(() => {
      setIsEditing(false)
      setExpanded(prev => !prev)
   }, [])

   // Auto-expand while user is editing so they can see what they're typing.
   // Collapse back to clamped state on blur.
   useEffect(() => {
      const el = contentRef.current
      if (!el) return
      const onFocusIn = () => {
         setIsEditing(true)
         if (needsExpansion) setExpanded(true)
      }
      const onFocusOut = (e: FocusEvent) => {
         if (el.contains(e.relatedTarget as Node)) return
         setIsEditing(false)
         // If focus moved to the toggle link (or elsewhere in the delivery note
         // block), let the click handler manage expanded state — don't reset it
         // here, otherwise focusout and toggle race (focusout sets false, toggle
         // flips it back to true).
         if (wrapperRef.current?.contains(e.relatedTarget as Node)) return
         setExpanded(false)
      }
      el.addEventListener('focusin', onFocusIn)
      el.addEventListener('focusout', onFocusOut)
      return () => {
         el.removeEventListener('focusin', onFocusIn)
         el.removeEventListener('focusout', onFocusOut)
      }
   }, [needsExpansion])

   // Hide the Lexical decorator wrapper node when delivery notes are toggled off.
   useEffect(() => {
      const el = wrapperRef.current
      if (!el) return
      const decoratorNode = el.parentElement
      if (decoratorNode) {
         decoratorNode.style.display = showDeliveryNotes ? '' : 'none'
      }
   }, [showDeliveryNotes])

   // Detect if content overflows the collapsed height.
   // When clamped (line-clamp active), only transition needsExpansion to true —
   // never back to false — to prevent oscillation (clamp shrinks scrollHeight
   // below threshold, removing clamp, growing again, ad infinitum).
   // When expanded (no line-clamp), scrollHeight is the real content height,
   // so it's safe to transition back to false when content shrinks below threshold.
   useEffect(() => {
      const el = contentRef.current
      if (!el || !showDeliveryNotes) return
      const observer = new ResizeObserver(() => {
         if (el.scrollHeight > EXPANSION_THRESHOLD) {
            setNeedsExpansion(true)
            if (isEditing) setExpanded(true)
         } else if (expanded) {
            setNeedsExpansion(false)
         }
      })
      observer.observe(el)
      return () => observer.disconnect()
   }, [showDeliveryNotes, isEditing, expanded])

   const clamped = needsExpansion && !expanded

   // Extracted as a variable because it's conditionally composed with spread operators
   // based on isReadOnly and clamped state — too complex to read inline.
   const contentSx = {
      color: colors.text,
      fontSize: '1rem',
      '& p': { margin: 0 },
      ...(!isReadOnly && {
         '& > div': { minHeight: '1.5em' },
         '& [class*="_nestedEditor"], & [class*="_editorRoot"], & [class*="_contentEditable"]': {
            backgroundColor: 'transparent',
         },
      }),
      ...(clamped && {
         display: '-webkit-box',
         WebkitLineClamp: COLLAPSED_LINES,
         WebkitBoxOrient: 'vertical',
         overflow: 'hidden',
      }),
   }

   return (
      <Box
         ref={wrapperRef}
         sx={{
            backgroundColor: colors.background,
            border: `2px solid ${colors.border}`,
            borderRadius: 2,
            p: 2,
            my: 2,
         }}
      >
         <Box
            sx={{
               display: 'flex',
               alignItems: 'center',
               gap: 1,
               mb: 2,
            }}
         >
            <InfoOutlinedIcon sx={{ fontSize: 22, color: colors.icon }} />
            <Typography
               variant="subtitle2"
               sx={{
                  fontWeight: 700,
                  color: colors.icon,
               }}
            >
               Delivery notes
            </Typography>
         </Box>

         <Box ref={contentRef} sx={contentSx}>
            <NestedLexicalEditor<ContainerDirective>
               block
               getContent={getContent}
               getUpdatedMdastNode={getUpdatedMdastNode}
            />
         </Box>
         {needsExpansion && (
            <GapLink onClick={toggle} underlineStyle="dashed" sx={{ mt: 1 }}>
               {expanded ? 'Read less' : 'Read more'}
            </GapLink>
         )}
      </Box>
   )
}

export const GapDeliveryNoteDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'containerDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: [],
   hasChildren: true,
   Editor: GapDeliveryNoteComponent,
}

export function InsertGapDeliveryNote() {
   const insertDirective = usePublisher(insertDirective$)

   const handleInsertClick = () => {
      insertDirective({
         name: DIRECTIVE_NAME,
         type: 'containerDirective',
      } as Parameters<typeof insertDirective>[0] & { children?: unknown })
   }

   return (
      <Tooltip title="Insert delivery notes">
         <IconButton size="small" onClick={handleInsertClick}>
            <InfoOutlinedIcon fontSize="small" />
         </IconButton>
      </Tooltip>
   )
}
